/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, memo, useMemo } from 'react';
import { Ellipse, Group, Line, Rect, Text } from 'react-konva';
import { Box } from 'entities/Box';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { PIXELS_PER_METER } from 'features/WarehouseMapConstructor/model/constants/mapScale';
import type { Shape } from 'features/WarehouseMapConstructor/model/types';
import { ShapeType } from 'features/WarehouseMapConstructor/model/types';
import { formatArea, getPolygonArea, getPolygonBounds } from 'features/WarehouseMapConstructor/utils/helpers/shapeGeometry';
import type { SizeFilter } from 'features/BoxSizeFilter/model/types';
import { getBoxUnavailableReason, type BookingMapUnpickableTapKind } from '../utils/helpers/getBookingMapUnpickableTapKind';

interface BookingMapShapesProps {
  shapes: Shape[];
  boxes: Box[] | undefined;
  selectedBoxId: string | undefined;
  selectedFilter: SizeFilter;
  allowedSizeCodeId?: string;
  highlightOnlySelected: boolean;
  onSelectBox: (box: Box | undefined, sizeCodeId?: string) => void;
  onPickBox: (box: Box, sizeCodeId: string) => void;
  notifyUnpickableTap: (kind: BookingMapUnpickableTapKind) => void;
  applyStageCursor: (cursor: string) => void;
  fontSize?: number;
}

const SELECTED_FILL = '#DEE8FF';
const SELECTED_STROKE = '#2058E7';
const OCCUPIED_FILL = '#FEE4E2';
const OCCUPIED_STROKE = '#EF4444';
const FREE_FILL = '#E2FEF1';
const FREE_STROKE = '#2BA865';
const DEFAULT_FILL = '#F3F6F9';
const DEFAULT_STROKE = '#788593';

interface ResolvedColors {
  fill: string;
  stroke: string;
}

interface ResolveColorParams {
  hasTargetBox: boolean;
  isBoxInactive: boolean;
  isSelected: boolean;
  highlightOnlySelected: boolean;
  isMatchedSizeGroup: boolean;
  isAllowedSizeCode: boolean;
  isOccupied: boolean;
}

const resolveColors = (params: ResolveColorParams): ResolvedColors => {
  if (!params.hasTargetBox) {
    return { fill: DEFAULT_FILL, stroke: DEFAULT_STROKE };
  }

  if (params.isBoxInactive) {
    return { fill: DEFAULT_FILL, stroke: DEFAULT_STROKE };
  }

  if (params.isSelected) {
    return { fill: SELECTED_FILL, stroke: SELECTED_STROKE };
  }

  if (params.highlightOnlySelected) {
    return { fill: DEFAULT_FILL, stroke: DEFAULT_STROKE };
  }

  if (params.isOccupied) {
    return { fill: OCCUPIED_FILL, stroke: OCCUPIED_STROKE };
  }

  if (params.isMatchedSizeGroup && params.isAllowedSizeCode) {
    return { fill: FREE_FILL, stroke: FREE_STROKE };
  }

  return { fill: DEFAULT_FILL, stroke: DEFAULT_STROKE };
};

export const BookingMapShapes: FC<BookingMapShapesProps> = memo((props) => {
  const {
    shapes,
    boxes,
    selectedBoxId,
    selectedFilter,
    allowedSizeCodeId,
    highlightOnlySelected,
    onSelectBox,
    onPickBox,
    notifyUnpickableTap,
    applyStageCursor,
    fontSize = 17,
  } = props;

  const boxById = useMemo(() => {
    if (!boxes?.length) {
      return new Map<string, Box>();
    }

    return new Map(boxes.map((box) => [box.boxId, box]));
  }, [boxes]);

  return (
    <>
      {shapes.map((shape) => {
        const resolvedFontSize = 'fontSize' in shape ? shape.fontSize ?? fontSize : fontSize;
        const targetBox = boxById.get(shape.id);
        const sizeCodeLabel = targetBox
          ? `${targetBox.sizeCode.square} ${getSizeCodeTypeString(targetBox.sizeCode.sizeCodeType)}`
          : undefined;
        const boxText = 'label' in shape && 'width' in shape ? `${shape.label}\n${sizeCodeLabel ?? ''}`.trim() : '';
        const isOccupied = Boolean(targetBox?.contractNumber || targetBox?.reservationNumber);
        const isBoxInactive = Boolean(targetBox && !targetBox.isActive);
        const isMatchedSizeGroup = selectedFilter !== 'All' ? targetBox?.sizeCode.sizeGroup === selectedFilter : true;
        const isAllowedSizeCode = !allowedSizeCodeId || targetBox?.sizeCode.sizeCodeId === allowedSizeCodeId;
        const isSelected = selectedBoxId === shape.id;
        const canPickBox =
          Boolean(targetBox?.isActive) &&
          !isOccupied &&
          isAllowedSizeCode &&
          !highlightOnlySelected &&
          (selectedFilter === 'All' || isMatchedSizeGroup);
        const pointerCursor = Boolean(targetBox && !isBoxInactive && (isSelected || canPickBox));

        const colors = resolveColors({
          hasTargetBox: Boolean(targetBox),
          isBoxInactive,
          isSelected,
          highlightOnlySelected,
          isMatchedSizeGroup,
          isAllowedSizeCode,
          isOccupied,
        });

        const handleShapeSelect = (): void => {
          const unpickableKind = getBoxUnavailableReason({
            targetBox,
            canPickBox,
            isSelected,
            highlightOnlySelected,
            isAllowedSizeCode,
            isOccupied,
            selectedFilter,
          });

          if (unpickableKind) {
            notifyUnpickableTap(unpickableKind);
          }

          if (!targetBox || !isAllowedSizeCode || highlightOnlySelected) {
            return;
          }

          if (isSelected) {
            onSelectBox(undefined);

            return;
          }

          if (canPickBox) {
            onPickBox(targetBox, targetBox.sizeCode.sizeCodeId);
          }
        };

        const onMouseEnter = (): void => {
          applyStageCursor(pointerCursor ? 'pointer' : '');
        };
        const onMouseLeave = (): void => {
          applyStageCursor('');
        };

        switch (shape.type) {
          case ShapeType.RECTANGLE:
            return (
              <Group
                key={shape.id}
                name="shape"
                id={shape.id}
                x={shape.x}
                y={shape.y}
                scaleX={shape.scaleX}
                scaleY={shape.scaleY}
                width={shape.width}
                height={shape.height}
                rotation={shape.rotation}
                onClick={handleShapeSelect}
                onTap={handleShapeSelect}
              >
                <Rect
                  width={shape.width}
                  height={shape.height}
                  fill={colors.fill}
                  stroke={colors.stroke}
                  strokeWidth={shape.strokeWidth}
                  cornerRadius={shape.cornerRadius}
                  strokeScaleEnabled={false}
                  cursor={pointerCursor ? 'pointer' : 'default'}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
                <Text
                  text={boxText}
                  x={(shape.labelOffsetX ?? 0) + shape.width / 2}
                  y={(shape.labelOffsetY ?? 0) + shape.height / 2}
                  width={shape.width}
                  height={shape.height}
                  offsetX={shape.width / 2}
                  offsetY={shape.height / 2}
                  rotation={shape.labelRotation ?? 0}
                  fontSize={resolvedFontSize}
                  align="center"
                  verticalAlign="middle"
                  listening={false}
                />
              </Group>
            );

          case ShapeType.LINE:
            return (
              <Line
                key={shape.id}
                id={shape.id}
                name="shape"
                fill={shape.fill}
                stroke={shape.stroke}
                strokeWidth={shape.strokeWidth}
                x={0}
                y={0}
                points={shape.points}
                scaleX={shape.scaleX}
                scaleY={shape.scaleY}
                rotation={shape.rotation}
              />
            );

          case ShapeType.CIRCLE: {
            const circleText = `${shape.label}\n${
              sizeCodeLabel ?? formatArea(Math.PI * (shape.radiusX / PIXELS_PER_METER) * (shape.radiusY / PIXELS_PER_METER))
            }`;

            return (
              <Group
                key={shape.id}
                name="shape"
                id={shape.id}
                x={shape.x}
                y={shape.y}
                scaleX={shape.scaleX}
                scaleY={shape.scaleY}
                width={shape.radiusX * 2}
                height={shape.radiusY * 2}
                rotation={shape.rotation}
                onClick={handleShapeSelect}
                onTap={handleShapeSelect}
              >
                <Ellipse
                  fill={colors.fill}
                  stroke={colors.stroke}
                  radiusX={shape.radiusX}
                  radiusY={shape.radiusY}
                  strokeWidth={shape.strokeWidth}
                  cornerRadius={shape.cornerRadius}
                  strokeScaleEnabled={false}
                  width={shape.radiusX * 2}
                  height={shape.radiusY * 2}
                  cursor={pointerCursor ? 'pointer' : 'default'}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
                <Text
                  text={circleText}
                  x={0}
                  y={0}
                  width={shape.radiusX * 2}
                  height={shape.radiusY * 2}
                  offsetX={shape.radiusX}
                  offsetY={shape.radiusY}
                  rotation={shape.labelRotation ?? 0}
                  fontSize={resolvedFontSize}
                  align="center"
                  verticalAlign="middle"
                  listening={false}
                />
              </Group>
            );
          }

          case ShapeType.TEXT:
            return <Text key={shape.id} name="shape" {...shape} text={shape.label} fontSize={resolvedFontSize} strokeWidth={1} />;

          case ShapeType.POLYGON:
            return <Line key={shape.id} name="shape" {...shape} x={0} y={0} closed />;

          case ShapeType.POLYGON_BOX: {
            const bounds = getPolygonBounds(shape.points);
            const polygonLabelOffsets = shape as { labelOffsetX?: number; labelOffsetY?: number };
            const labelX = bounds.minX + (polygonLabelOffsets.labelOffsetX ?? 0);
            const labelY = bounds.minY + (polygonLabelOffsets.labelOffsetY ?? 0);
            const polygonBoxText = `${shape.label}\n${sizeCodeLabel ?? formatArea(getPolygonArea(shape.points, PIXELS_PER_METER))}`;

            return (
              <Group
                key={shape.id}
                name="shape"
                id={shape.id}
                rotation={shape.rotation}
                onClick={handleShapeSelect}
                onTap={handleShapeSelect}
              >
                <Line
                  points={shape.points}
                  fill={colors.fill}
                  stroke={colors.stroke}
                  strokeWidth={shape.strokeWidth}
                  scaleX={shape.scaleX}
                  scaleY={shape.scaleY}
                  x={0}
                  y={0}
                  closed
                  cursor={pointerCursor ? 'pointer' : 'default'}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
                <Text
                  text={polygonBoxText}
                  x={labelX + bounds.width / 2}
                  y={labelY + bounds.height / 2}
                  width={bounds.width}
                  height={bounds.height}
                  offsetX={bounds.width / 2}
                  offsetY={bounds.height / 2}
                  rotation={shape.labelRotation ?? 0}
                  fontSize={resolvedFontSize}
                  align="center"
                  verticalAlign="middle"
                  listening={false}
                />
              </Group>
            );
          }

          default:
            return undefined;
        }
      })}
    </>
  );
});

BookingMapShapes.displayName = 'BookingMapShapes';
